/**
 * frontend/src/pages/valuation/_NormalizeBalanceSheet.tsx
 *
 * VAL-U2C-SCREEN-1 — total assets and total liabilities, typed by the signer.
 * After Save the line "Stored: ..." is what the server returned on read-back.
 * VAL-PATH-ADJUST P4b — the heading names the as-of year (owner-approved 10-21 20:53).
 */
import { useState } from 'react'
import type { BalanceSheet } from '@/api/valuationNormalization'
import { money } from './_NormalizeYearTable'
import { HelpDot } from '@/components/ui/help-dot'
import { HELP_TEXTS } from './_helpTexts'

export const balanceSheetHeading = (year: number) => `Balance sheet as of the end of FY${year}`

const FIELD = 'mt-1 block w-full rounded border border-pal-line-strong px-2 py-1 text-sm'

interface Props {
  stored: BalanceSheet | null
  asOfYear?: number
  busy: boolean
  onSave: (body: { total_assets: number; total_liabilities: number; as_of_year: number | null }) => Promise<void>
}

export function NormalizeBalanceSheet({ stored, asOfYear, busy, onSave }: Props) {
  const [assets, setAssets] = useState(stored ? String(stored.total_assets) : '')
  const [liabilities, setLiabilities] = useState(stored ? String(stored.total_liabilities) : '')
  const yearShown = stored?.as_of_year ?? asOfYear
  const ready =
    assets.trim() !== '' && liabilities.trim() !== '' && Number.isFinite(Number(assets)) && Number.isFinite(Number(liabilities))
  return (
    <section className="space-y-2">
      <h2 className="text-sm font-semibold text-pal-ink">
        {yearShown ? balanceSheetHeading(yearShown) : 'Balance sheet'}
      </h2>
      <div className="grid grid-cols-2 gap-3">
        <div className="text-xs text-pal-ink-soft">
          <div className="flex items-center gap-1">
            <label htmlFor="bs-total_assets">Total assets</label>
            <HelpDot label="Total assets" paragraphs={HELP_TEXTS['bs-total_assets']} testId="info-bs-total_assets" />
          </div>
          <input id="bs-total_assets" type="number" className={FIELD} value={assets} onChange={e => setAssets(e.target.value)} />
        </div>
        <div className="text-xs text-pal-ink-soft">
          <div className="flex items-center gap-1">
            <label htmlFor="bs-total_liabilities">Total liabilities</label>
            <HelpDot label="Total liabilities" paragraphs={HELP_TEXTS['bs-total_liabilities']} testId="info-bs-total_liabilities" />
          </div>
          <input id="bs-total_liabilities" type="number" className={FIELD} value={liabilities} onChange={e => setLiabilities(e.target.value)} />
        </div>
      </div>
      <button
        type="button"
        disabled={!ready || busy}
        onClick={() => void onSave({
          total_assets: Number(assets),
          total_liabilities: Number(liabilities),
          as_of_year: stored?.as_of_year ?? asOfYear ?? null,
        })}
        className="rounded-md border border-pal-line-strong bg-pal-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-pal-surface-subtle disabled:opacity-50"
      >
        Save balance sheet
      </button>
      {stored && (
        <p className="text-xs text-pal-ink-muted">
          Stored: assets {money(stored.total_assets)}, liabilities {money(stored.total_liabilities)}
        </p>
      )}
    </section>
  )
}
